/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { Route } from 'vue-router';

/**
 * URL param handler for user preferences for listing components.
 *
 * Usage:
 * 1. Add the mixin to a page component containing at least one listing component
 *    import UrlParamHandler from '@/wherever/UrlParamHandler';
 *    export default Vue.extend({
 *      name: 'YourPageComponent',
 *      mixins: [UrlParamHandler],
 *      ...
 *    })
 * 2. Use the `userPreferencesForId` method to get user preferences from the URL
 *    for the given listing id, i.e.:
 *    <sor-listing
 *      ...
 *      :user-preferences="userPreferencesForId('sor')"
 *    />
 * 3. Use the `userPreferencesChange` method to update the user preferences in
 *    the URL for the given listing id, i.e.:
 *    <sor-listing
 *      ...
 *      @userPreferencesChange="userPreferencesChange('sor', $event)"
 *    />
 */
// TODO: remove console statements
// TODO: add specific types
// TODO: add tests
export default {
  data() {
    return {
      // Contains user preferences per listing id
      // TODO: add type
      userPreferences: {},
    };
  },
  watch: {
    $route: {
      immediate: true,
      handler: function(to: Route): void {
        const userPreferences: string | undefined = to.query['p'];

        if (!userPreferences) return;

        this.setUserPreferences(userPreferences);
      },
    },
  },
  methods: {
    resetUserPreferences(): void {
      this.userPreferences = {};
    },
    userPreferencesForId(listingId: string): object {
      return this.userPreferences[listingId];
    },
    setUserPreferences(encryptedUserPreferences: string): void {
      this.userPreferences = this.decodeUserPreferences(encryptedUserPreferences);
    },
    userPreferencesChange(listingId: string, userPreferences: object): void {
      const hasListingViewPreferences = (obj: Object) => Object.keys(obj).length > 0;

      if (hasListingViewPreferences(userPreferences)) {
        this.userPreferences[listingId] = userPreferences;
      } else {
        delete this.userPreferences[listingId];
      }

      this.updateUserPreferencesInUrl(this.userPreferences);
    },
    updateUserPreferencesInUrl(userPreferences: object) {
      const hasPreferencesForAnyListingView = (obj: Object) => Object.keys(obj).length > 0;

      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            p: hasPreferencesForAnyListingView(userPreferences)
              ? this.encodeUserPreferences(userPreferences)
              : undefined,
          },
        },
      });
    },
    // TODO: move these to own mixin and import in this one, this can be reused
    encodeUserPreferences(preferences: object): string {
      let encodedUserPreferences = '';

      try {
        // TODO: check each step and handle each possible error
        encodedUserPreferences = btoa(unescape(encodeURIComponent(JSON.stringify(preferences))));
      } catch {
        this.showAlert({
          type: 'error',
          message: 'Unable to set new user preferences.',
        });
      }

      return encodedUserPreferences;
    },
    decodeUserPreferences(preferences: string): object {
      let decodedUserPreferences = {};

      try {
        // TODO: check each step and handle each possible error
        decodedUserPreferences = JSON.parse(decodeURIComponent(escape(atob(preferences))));
      } catch {
        this.showAlert({
          type: 'error',
          message: 'Unable to use existing user preferences.',
        });
      }

      return decodedUserPreferences;
    },
  },
};
